import type { Thread, AuthUser } from "@/types"
import { SERVICE_REGISTRY, canonicalServiceId } from "@/services/services.registry"

/**
 * View-model that maps a real backend Thread onto the rich fields the
 * redesigned tables/cards expect. Fields the backend does not yet provide
 * (demand amount, officer) fall back gracefully rather than being faked.
 */
export interface CaseVM {
  id: string
  title: string
  ref: string
  type: "OIO" | "SCN" | "OIA" | "RFD"
  typeLabel: string
  status: "review" | "draft" | "filed" | "progress" | "error"
  statusLabel: string
  amount: string
  updated: string
  officer: string
}

// Built order types take their label/icon from the service registry so the
// case lists, the picker and the workspace cannot drift apart.
const TYPE_LABELS: Record<CaseVM["type"], string> = {
  OIO: SERVICE_REGISTRY.oio.title,
  SCN: "SCN response",
  OIA: SERVICE_REGISTRY.oia.title,
  RFD: "Refund sanction",
}

export const TYPE_ICON: Record<string, string> = {
  OIO: SERVICE_REGISTRY.oio.icon,
  SCN: "reply",
  OIA: SERVICE_REGISTRY.oia.icon,
  RFD: "receipt",
}

/** Maps draft_status (thread metadata / agent) → a vault status + pill tone. */
export const STATUS_PILL: Record<CaseVM["status"], "upheld" | "favour" | "partial" | "neutral"> = {
  review: "partial",
  draft: "neutral",
  progress: "partial",
  filed: "favour",
  error: "upheld",
}

// Labels follow an adjudicating officer's mental model of an OIO's life:
//   draft pending → drafting → ready for the officer's review → issued.
function deriveStatus(
  thread: Thread,
  type: CaseVM["type"],
): { status: CaseVM["status"]; label: string } {
  const ds = String(thread.metadata?.draft_status ?? "")
  if (type === "OIA") return deriveAppealStatus(ds)
  switch (ds) {
    case "draft_running":
    case "draft_starting":
      return { status: "progress", label: "Drafting…" }
    case "draft_completed":
      return { status: "review", label: "Ready for review" }
    case "draft_filed":
      return { status: "filed", label: "Filed" }
    case "draft_failed_terminal":
      return { status: "error", label: "Needs attention" }
    default:
      return { status: "draft", label: "Draft pending" }
  }
}

// An appeal speaks its own draft_status vocabulary (design 5.4), so the OIO
// switch above would label every appeal "Draft pending". Draft run, then
// revise runs: drafting → ready → (revising → ready)* → filed. The two
// "blocked" outcomes are delivered states the officer must act on, not
// crashes, so they say what is blocked rather than "Needs attention".
function deriveAppealStatus(ds: string): { status: CaseVM["status"]; label: string } {
  switch (ds) {
    case "drafting":
      return { status: "progress", label: "Drafting…" }
    case "revising":
      return { status: "progress", label: "Revising…" }
    case "draft_ready":
      return { status: "review", label: "Ready for review" }
    case "draft_flagged":
      // Not an error: the order was delivered. It is not signable yet, and
      // the officer has points to decide — so it reads as work to do, not as
      // a failure and not as "pending" (nothing is pending; it is ready to
      // read).
      return { status: "review", label: "Attention needed" }
    case "draft_blocked":
      return { status: "error", label: "Draft blocked" }
    case "intake_blocked":
      return { status: "error", label: "Intake blocked" }
    case "failed":
      return { status: "error", label: "Needs attention" }
    case "draft_filed":
      return { status: "filed", label: "Filed" }
    default:
      return { status: "draft", label: "Draft pending" }
  }
}

function caseType(rawServiceId: unknown): CaseVM["type"] {
  // canonicalServiceId maps the legacy "apl" placeholder to "oia".
  switch (canonicalServiceId(rawServiceId)) {
    case "scn":
      return "SCN"
    case "oia":
      return "OIA"
    case "rfd":
      return "RFD"
    default:
      return "OIO"
  }
}

function initials(name: string | null | undefined, email?: string): string {
  const src = (name ?? "").trim() || (email ?? "").split("@")[0] || ""
  const parts = src.split(/[\s._-]+/).filter(Boolean)
  if (parts.length === 0) return "—"
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function fmtDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
}

/**
 * Fallback total demand for cases drafted before `demand_amount` was written
 * to thread metadata: sum the per-allegation demands from the findings cache
 * OIOWorkspace persists per case.
 */
// toCaseVM runs per thread PER RENDER (nav + route), and during a replay
// burst that is thousands of renders — a JSON.parse of the full findings
// blob each time blocked the main thread for seconds on large cases. The
// parse result is cached per thread, invalidated by the raw string's
// length (localStorage returns fresh strings, so identity can't be used;
// a same-length different-content write is not a case that occurs — the
// findings cache is only ever replaced wholesale).
const demandCache = new Map<string, { len: number; value: string | null }>()

function demandFromCachedFindings(threadId: string): string | null {
  if (typeof localStorage === "undefined") return null
  try {
    const raw = localStorage.getItem(`oio-findings-${threadId}`)
    if (!raw) return null
    const cached = demandCache.get(threadId)
    if (cached && cached.len === raw.length) return cached.value
    const findings = JSON.parse(raw) as Array<{
      demand_computation?: { total_demand?: number }
    }>
    if (!Array.isArray(findings)) return null
    const total = findings.reduce(
      (sum, f) => sum + (Number(f?.demand_computation?.total_demand) || 0),
      0,
    )
    const value = total > 0 ? "₹ " + total.toLocaleString("en-IN") : null
    demandCache.set(threadId, { len: raw.length, value })
    return value
  } catch {
    return null
  }
}

export function toCaseVM(thread: Thread, user?: AuthUser | null): CaseVM {
  const type = caseType(thread.metadata?.service_id)
  const { status, label } = deriveStatus(thread, type)
  const year = new Date(thread.created_at).getFullYear() || new Date().getFullYear()
  const shortId = thread.id.replace(/-/g, "").slice(-4).toUpperCase()
  // The findings-cache fallback is an OIO artefact (OIOWorkspace writes
  // oio-findings-{id}); an appeal never has one, so skip the localStorage read.
  const amount =
    (thread.metadata?.demand_amount as string | undefined) ??
    (type === "OIO" ? demandFromCachedFindings(thread.id) : null) ??
    "—"

  return {
    id: thread.id,
    title: thread.title || "Untitled case",
    ref: `${type} / ${year} / ${shortId}`,
    type,
    typeLabel: TYPE_LABELS[type],
    status,
    statusLabel: label,
    amount,
    updated: fmtDate(thread.updated_at || thread.created_at),
    officer: initials(user?.displayName, user?.email),
  }
}
